import { Link } from 'react-router-dom';
import { events } from '@/mocks/events';
import type { HotEvent } from '@/mocks/events';
import { StatusBadge, CategoryTag, heatLevel } from '@/components/feature/EventBits';

function BigCard({ event }: { event: HotEvent }) {
  const first = event.nodes[0];
  const last = event.nodes[event.nodes.length - 1];
  return (
    <article className="grid gap-0 overflow-hidden rounded-xl border border-background-200 bg-background-50 sm:grid-cols-5">
      <div className="relative sm:col-span-2">
        <img src={event.cover} alt={event.coverAlt} className="h-52 w-full object-cover sm:h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent sm:bg-gradient-to-r" />
      </div>
      <div className="flex flex-col p-5 sm:col-span-3 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={event.status} />
          <CategoryTag category={event.category} />
          <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
            <i className="ri-fire-fill text-[11px]" /> 焦点事件
          </span>
        </div>
        <Link to={`/event/${event.id}`}>
          <h2 className="mt-3 font-heading text-xl font-semibold leading-snug text-foreground-950 transition-colors hover:text-primary-700 md:text-2xl">
            {event.title}
          </h2>
        </Link>
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600 md:line-clamp-3">{event.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1"><i className="ri-play-circle-line text-accent-500" />爆发 {first.date.slice(5).replace('-', '/')}</span>
          <span className="flex items-center gap-1"><i className="ri-focus-3-line text-primary-500" />最新节点 {last.date.slice(5).replace('-', '/')}</span>
          <span className="flex items-center gap-1"><i className="ri-node-tree" />{event.nodes.length} 个节点</span>
        </div>
        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="text-xs text-foreground-500">热度 <b className="ml-1 text-sm text-primary-600 tabular-nums">{event.heat}</b> · {heatLevel(event.heat)}</span>
          <Link
            to={`/event/${event.id}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看完整时间轴 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function TrendSection() {
  const featured = events.find((e) => e.featured) || [...events].sort((a, b) => b.heat - a.heat)[0];
  const hotList = [...events].filter((e) => e.id !== featured.id).sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <section id="live" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary-600">
            <i className="ri-flashlight-line" /> LIVE 追更中
          </span>
          <h2 className="mt-2 flex items-center gap-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            正在发酵的焦点事件
            <span className="flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 pulse-dot" />
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 pulse-dot" style={{ animationDelay: '0.2s' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500 pulse-dot" style={{ animationDelay: '0.4s' }} />
            </span>
          </h2>
        </div>
        <Link to="/events" className="hidden items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 sm:inline-flex">
          全部事件 <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <BigCard event={featured} />
        </div>
        <aside className="rounded-xl border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">热度榜</h3>
            <span className="flex items-center gap-1 text-xs text-foreground-400"><i className="ri-fire-line text-primary-500" /> 综合热度</span>
          </div>
          <ol className="mt-4 space-y-1">
            {hotList.map((ev, i) => (
              <li key={ev.id}>
                <Link to={`/event/${ev.id}`} className="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-100">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold tabular-nums ${
                      i === 0 ? 'bg-primary-500 text-background-50' : i === 1 ? 'bg-accent-500 text-foreground-950' : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">{ev.title}</p>
                    <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-400">
                      <span>{ev.category}</span>
                      <span className="flex items-center gap-1">
                        <span className={`h-1.5 w-1.5 rounded-full ${ev.status === '发酵中' ? 'bg-accent-500' : 'bg-secondary-300'}`} />
                        {ev.status}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs font-semibold text-primary-600 tabular-nums">{ev.heat}</span>
                    <div className="h-1 w-14 overflow-hidden rounded-full bg-secondary-100">
                      <div className="h-full rounded-full bg-gradient-to-r from-accent-400 to-primary-500" style={{ width: `${ev.heat}%` }} />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </section>
  );
}